/**
 * Navigation Selector
 * 
 * Dynamically renders the appropriate navigation component
 * based on the current layout theme and number of navigation items.
 * Automatically switches to mega-menu for sites with 10+ links.
 */

import { useMemo } from "react";
import { useLayout } from "@/lib/layout-provider";
import { useNavigationItems } from "@/hooks/useNavigationItems";
import { getOptimalNavigationStyle } from "@/lib/navigation-utils";
import NavigationTopBar from "./NavigationTopBar";
import NavigationSidebar from "./NavigationSidebar";
import NavigationCollapsible from "./NavigationCollapsible";
import NavigationCenteredSplit from "./NavigationCenteredSplit";
import NavigationMegaMenu from "./NavigationMegaMenu";
import type { NavigationStyle } from "@/lib/layout-types";

const navigationComponents: Record<NavigationStyle, React.ComponentType> = {
  'top-bar': NavigationTopBar,
  'sidebar-left': NavigationSidebar,
  'sidebar-collapsible': NavigationCollapsible,
  'centered-split': NavigationCenteredSplit,
  'mega-menu': NavigationMegaMenu,
};

const NavigationSelector = () => {
  const { currentLayout } = useLayout();
  const { navigationItems } = useNavigationItems();
  
  // Intelligently determine navigation style based on item count
  // Only override if user hasn't explicitly set a sidebar style
  const effectiveStyle = useMemo<NavigationStyle>(() => {
    const configuredStyle = currentLayout.navigation.style;
    const itemCount = navigationItems.length;
    
    // Don't override sidebar styles (they're intentional design choices)
    if (configuredStyle === 'sidebar-left' || configuredStyle === 'sidebar-collapsible') {
      return configuredStyle;
    }
    
    // For top-bar and centered-split, auto-switch to mega-menu if many items
    if (itemCount >= 10 && (configuredStyle === 'top-bar' || configuredStyle === 'centered-split')) {
      return 'mega-menu';
    }
    
    // Otherwise use configured style
    return configuredStyle;
  }, [currentLayout.navigation.style, navigationItems.length]);
  
  const NavigationComponent = navigationComponents[effectiveStyle] || NavigationTopBar;

  return <NavigationComponent />;
};

export default NavigationSelector;





